/** localStorage draft for /survey/prevbrandanswer. */
export const PREV_BRAND_ANSWER_STORAGE_KEY = 'ldsurvey:prevBrandAnswer';

export type ElaboratedReason = {
  checked: boolean;
  elaboration: string;
};

export type PrevBrandAnswerAnswers = {
  taste: ElaboratedReason;
  price: ElaboratedReason;
  productFeatures: ElaboratedReason;
  brandCredibility: ElaboratedReason;
  peersSmoking: ElaboratedReason;
  otherReason: string;
  smokedOtherProduct: '' | 'yes' | 'no';
};

const emptyElaborated = (): ElaboratedReason => ({ checked: false, elaboration: '' });

export const emptyPrevBrandAnswerAnswers = (): PrevBrandAnswerAnswers => ({
  taste: emptyElaborated(),
  price: emptyElaborated(),
  productFeatures: emptyElaborated(),
  brandCredibility: emptyElaborated(),
  peersSmoking: emptyElaborated(),
  otherReason: '',
  smokedOtherProduct: '',
});

function parseElaborated(value: unknown): ElaboratedReason {
  // Back-compat: older drafts stored brandCredibility / peersSmoking as booleans.
  if (value === true) return { checked: true, elaboration: '' };
  if (value === false || value == null) return emptyElaborated();
  if (typeof value !== 'object') return emptyElaborated();
  const o = value as Record<string, unknown>;
  return {
    checked: o.checked === true,
    elaboration: typeof o.elaboration === 'string' ? o.elaboration : '',
  };
}

export function readPrevBrandAnswerAnswers(): PrevBrandAnswerAnswers {
  try {
    const raw = localStorage.getItem(PREV_BRAND_ANSWER_STORAGE_KEY);
    if (!raw) return emptyPrevBrandAnswerAnswers();
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return emptyPrevBrandAnswerAnswers();
    const o = parsed as Record<string, unknown>;
    const smoked =
      o.smokedOtherProduct === 'yes' || o.smokedOtherProduct === 'no' ? o.smokedOtherProduct : '';
    return {
      taste: parseElaborated(o.taste),
      price: parseElaborated(o.price),
      productFeatures: parseElaborated(o.productFeatures),
      brandCredibility: parseElaborated(o.brandCredibility),
      peersSmoking: parseElaborated(o.peersSmoking),
      otherReason: typeof o.otherReason === 'string' ? o.otherReason : '',
      smokedOtherProduct: smoked,
    };
  } catch {
    return emptyPrevBrandAnswerAnswers();
  }
}

export function writePrevBrandAnswerAnswers(data: PrevBrandAnswerAnswers): void {
  try {
    localStorage.setItem(PREV_BRAND_ANSWER_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearPrevBrandAnswerAnswers(): void {
  try {
    localStorage.removeItem(PREV_BRAND_ANSWER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export type PrevBrandReasonType =
  | 'taste'
  | 'price'
  | 'productFeatures'
  | 'brandCredibility'
  | 'peersSmoking'
  | 'other';

export type PrevBrandReasonSelection = {
  type: PrevBrandReasonType;
  elaboration: string;
};

const ELABORATED_KEYS = [
  'taste',
  'price',
  'productFeatures',
  'brandCredibility',
  'peersSmoking',
] as const;

function hasAnyReasonSelected(data: PrevBrandAnswerAnswers): boolean {
  return (
    ELABORATED_KEYS.some((key) => data[key].checked) || data.otherReason.trim().length > 0
  );
}

function elaboratedReasonComplete(data: PrevBrandAnswerAnswers): boolean {
  return ELABORATED_KEYS.every(
    (key) => !data[key].checked || data[key].elaboration.trim().length > 0
  );
}

export function isPrevBrandAnswerComplete(): boolean {
  const data = readPrevBrandAnswerAnswers();
  if (!hasAnyReasonSelected(data)) return false;
  if (!elaboratedReasonComplete(data)) return false;
  return data.smokedOtherProduct === 'yes' || data.smokedOtherProduct === 'no';
}

export function getSelectedPrevBrandReasons(
  data: PrevBrandAnswerAnswers
): PrevBrandReasonSelection[] {
  const selected: PrevBrandReasonSelection[] = [];
  for (const key of ELABORATED_KEYS) {
    if (data[key].checked) {
      selected.push({ type: key, elaboration: data[key].elaboration });
    }
  }
  const other = data.otherReason.trim();
  if (other.length > 0) {
    selected.push({ type: 'other', elaboration: other });
  }
  return selected;
}
